/**
 * The pure QR Code Model 2 encoder: byte mode only (ISO/IEC 18004 §7.4.5 —
 * sufficient for this library's target payload shape, an opaque token, and
 * simpler than numeric/alphanumeric mode selection since every input byte
 * maps 1:1 to 8 data bits with no compaction table). Deterministic — no
 * `Date`, no randomness, no I/O — so `qrToSvg(value, options)` produces
 * byte-identical output for the same input every time, which is what lets a
 * PDF/email/in-app ticket-template render the same SVG inline regardless of
 * when it's generated.
 *
 * Default error-correction level is `Q` (~25% recovery). A door-scan
 * context — a printed or on-screen ticket read by a variety of phone
 * cameras, sometimes creased, glare-lit, or partially obscured by the
 * ticket's own design elements sitting near the code — benefits from more
 * headroom than `M`'s ~15%, and this library's actual payload (a 32-hex-char
 * token, i.e. 32 bytes) is small enough that `Q` barely costs anything: it
 * still fits Version 3 (29x29 modules), one version step above what `M`
 * needs. `H` (~30%) is reserved for a caller with a documented reason to
 * spend the extra module density; `L` (~7%) trades away robustness this
 * library's own admission-ticket consumer explicitly wants.
 */
import { buildDataCodewords, buildFinalBitStream, chooseVersion } from './bit-stream'
import { buildModuleMatrix, matrixSize } from './matrix'
import type { QrErrorCorrectionLevel, QrMatrix } from './types'

export type { EccBlockInfo, QrErrorCorrectionLevel, QrMatrix, QrToSvgOptions } from './types'

/**
 * Encodes `value` (byte mode) into a QR Code Model 2 module matrix at the
 * given error-correction level. Pure and dependency-light — no DOM, no
 * Node-only API — so it runs in a browser, in SSR, or in a plain Node
 * script with identical output. Anything but a level is the default, `Q`.
 */
export function encodeQr(value: string, level?: QrErrorCorrectionLevel | null): QrMatrix {
	const errorCorrectionLevel = level === 'L' || level === 'M' || level === 'H' ? level : 'Q'
	const dataBytes = [...new TextEncoder().encode(value)]
	const version = chooseVersion(dataBytes.length, errorCorrectionLevel)
	const dataCodewords = buildDataCodewords(dataBytes, version, errorCorrectionLevel)
	const finalBits = buildFinalBitStream(dataCodewords, version, errorCorrectionLevel)
	const modules = buildModuleMatrix(finalBits, version, errorCorrectionLevel)
	return { size: matrixSize(version), version, errorCorrectionLevel, modules }
}
